import React, { useCallback, useEffect, useState, useRef } from "react";

import { ClassificationCode } from "@/types/code.types";
import {
  type ParsedJsonContentType,
  TableOrImageContentType,
} from "@/types/fillInTableContent.types";
import { usePressLabelsValue } from "@stores/pressLabels";

import FillInTableArticleImageContainer from "./components/FillInTableArticleImageContainer/FillInTableArticleImageContainer";
import FillInTableArticleTableContainer from "./components/FillInTableArticleTableContainer/FillInTableArticleTableContainer";
import PressLabelElement from "./components/PressLabelElement/PressLabelElement";
import { useCheckBoldKeywords } from "./hooks/useCheckBoldKeywords";
import { useSliceLabelData } from "./hooks/useSliceLabelData";
import * as S from "./PressLabelViewer.style";

import type { PressLabel } from "@/types/pressLabel.type";

interface Props {
  parsedContentArray: ParsedJsonContentType[];
  draftArticleId: number;
  handleLabelClick?: (
    label: PressLabel,
  ) => React.MouseEventHandler<HTMLElement>;
  handleViewerClick?: React.MouseEventHandler<HTMLDivElement>;
  columnLabelViewerWidth?: number;
  columnLabelViewerTop?: number;
  classificationManual: ClassificationCode;
  additionalBoldKeywords: string[];
  searchTerm?: string;
  onSearchResultsCount?: (count: number) => void;
  currentSearchIndex?: number;
  focusedStartIndex?: number | null;
  isClassificationPage?: boolean;

  onFocusInnerNodesChange?: (nodes: React.ReactNode[]) => void;
}

// NOTE : 정규식에서 의미가 있는 특수문자들의 경우, 입력하면 검색이 안되어 추가
const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const PressLabelViewer = ({
  parsedContentArray,
  draftArticleId,
  handleLabelClick,
  handleViewerClick,
  columnLabelViewerWidth,
  columnLabelViewerTop,
  classificationManual,
  additionalBoldKeywords,
  searchTerm,
  onSearchResultsCount,
  currentSearchIndex = 0,
  focusedStartIndex = null,
  isClassificationPage = false,

  onFocusInnerNodesChange,
}: Props) => {
  const pressLabels = usePressLabelsValue(draftArticleId);
  const [innerNodes, setInnerNodes] = useState<React.ReactNode[]>([]);
  const prevInnerNodesRef = useRef<React.ReactNode[]>([]);
  const viewerRef = useRef<HTMLDivElement>(null);
  const { sliceLabelData, textContent } = useSliceLabelData(
    parsedContentArray,
    pressLabels,
  );

  const { checkBoldKeyword } = useCheckBoldKeywords(
    classificationManual,
    additionalBoldKeywords,
  );

  const isInitialRender = useRef(true);

  const [textSearchCount, setTextSearchCount] = useState(0);
  const [tableSearchCount, setTableSearchCount] = useState(0);
  const totalSearchCountRef = useRef(0);
  const tableSearchCountsRef = useRef<Record<string, number>>({});
  const prevSearchTermRef = useRef<string>("");

  const [tableSearchRanges, setTableSearchRanges] = useState<
    React.ReactElement[]
  >([]);

  const removeDuplicates = (objectList: React.ReactElement[]) => {
    const newObjectList = objectList.map((object) => {
      return JSON.stringify(object);
    });
    return [...new Set(newObjectList)].map((object) => JSON.parse(object));
  };

  const changeTableSearchRanges = useCallback(
    (ranges: React.ReactElement[]) => {
      if (!searchTerm) {
        setTableSearchRanges([]);
        return;
      }

      if (
        searchTerm?.toLowerCase() === ranges?.[0]?.props?.children.toLowerCase()
      ) {
        setTableSearchRanges((prev) => {
          if (
            prev.length > 0 &&
            prev[0]?.props?.children.toLowerCase() !== searchTerm.toLowerCase()
          ) {
            return ranges;
          }
          return removeDuplicates([...prev, ...ranges]);
        });
      }
      // setTableSearchRanges([]);
    },
    [searchTerm],
  );

  const handleAddTableNodes = useCallback(
    (contentItem: TableOrImageContentType, nodeIndex: number) => {
      if (contentItem.type === "table") {
        const tableKey = nodeIndex;
        return [
          <FillInTableArticleTableContainer
            key={`table-${tableKey}`}
            tableIndex={nodeIndex}
            item={contentItem}
            searchTerm={searchTerm}
            onSearchResultsCount={(count) => {
              if (prevSearchTermRef.current === searchTerm) {
                tableSearchCountsRef.current[tableKey] = count;
                const totalTableCount = Object.values(
                  tableSearchCountsRef.current,
                ).reduce((sum, currentCount) => sum + currentCount, 0);
                setTableSearchCount(totalTableCount);
                totalSearchCountRef.current = textSearchCount + totalTableCount;
                if (onSearchResultsCount) {
                  onSearchResultsCount(totalSearchCountRef.current);
                }
              }
            }}
            onSearchRangesUpdate={(ranges) => {
              if (prevSearchTermRef.current !== searchTerm) {
                setTableSearchRanges([]);
              }
              changeTableSearchRanges(ranges);
            }}
          />,
        ];
      }

      if (contentItem.type === "image")
        return [
          <FillInTableArticleImageContainer
            key={contentItem.source}
            item={contentItem}
          />,
        ];
    },
    [
      searchTerm,
      textSearchCount,
      onSearchResultsCount,
      changeTableSearchRanges,
    ],
  );

  const handleAddTextNodes = useCallback(
    (nodeValue: string, labels: PressLabel[], textStartIndex: number) => {
      let tempInnerNodes: React.ReactNode[] = [];

      if (labels.length === 0 && !searchTerm) {
        const boldRanges = checkBoldKeyword(nodeValue);

        let lastIndex = 0;
        const nodes: React.ReactNode[] = [];

        boldRanges.forEach(([start, end]) => {
          if (start > lastIndex) {
            nodes.push(
              <span key={`text-${lastIndex}-${start}`}>
                {nodeValue.slice(lastIndex, start)}
              </span>,
            );
          }
          nodes.push(
            <span key={`bold-${start}-${end}`} style={{ fontWeight: "bold" }}>
              {nodeValue.slice(start, end)}
            </span>,
          );
          lastIndex = end;
        });

        if (lastIndex < nodeValue.length) {
          nodes.push(
            <span key={`text-${lastIndex}-${nodeValue.length}`}>
              {nodeValue.slice(lastIndex)}
            </span>,
          );
        }

        tempInnerNodes = nodes;
      } else {
        const currentNodeValue = nodeValue;
        const regularLabels = [...labels];

        const allRanges: Array<[number, number, string[]]> = [];

        const boldRanges = checkBoldKeyword(currentNodeValue);

        boldRanges.forEach(([start, end]) => {
          allRanges.push([
            start + textStartIndex,
            end + textStartIndex,
            ["bold"],
          ]);
        });

        if (searchTerm) {
          const escapedSearchTerm = escapeRegExp(searchTerm);
          const regex = new RegExp(escapedSearchTerm, "gi");
          let match;
          regex.lastIndex = 0;

          // eslint-disable-next-line no-cond-assign
          while ((match = regex.exec(currentNodeValue)) !== null) {
            const matchStart = match.index + textStartIndex;
            const matchEnd = match.index + searchTerm.length + textStartIndex;
            allRanges.push([matchStart, matchEnd, ["search"]]);
          }
        }
        regularLabels.forEach((label) => {
          allRanges.push([
            label.start + textStartIndex,
            label.end + textStartIndex,
            ["label"],
          ]);
        });

        const points = new Set<number>();
        allRanges.forEach(([start, end]) => {
          points.add(start);
          points.add(end);
        });
        const sortedPoints = Array.from(points).sort((a, b) => a - b);

        const mergedRanges: Array<[number, number, string[]]> = [];
        for (let i = 0; i < sortedPoints.length - 1; i++) {
          const start = sortedPoints[i];
          const end = sortedPoints[i + 1];
          const types = new Set<string>();

          allRanges.forEach(([rangeStart, rangeEnd, rangeTypes]) => {
            if (start >= rangeStart && end <= rangeEnd) {
              rangeTypes.forEach((type) => types.add(type));
            }
          });

          if (types.size > 0) {
            mergedRanges.push([start, end, Array.from(types)]);
          }
        }

        const groupedSearchRanges: Array<[number, number, string[]][]> = [];
        let currentGroup: Array<[number, number, string[]]> = [];

        mergedRanges.forEach((range) => {
          if (range[2].includes("search")) {
            if (currentGroup.length === 0) {
              currentGroup.push(range);
            } else if (currentGroup[currentGroup.length - 1][1] === range[0]) {
              currentGroup.push(range);
            } else {
              if (currentGroup.length > 0) {
                groupedSearchRanges.push([...currentGroup]);
              }
              currentGroup = [range];
            }
          } else if (currentGroup.length > 0) {
            groupedSearchRanges.push([...currentGroup]);
            currentGroup = [];
          }
        });

        if (currentGroup.length > 0) {
          groupedSearchRanges.push([...currentGroup]);
        }

        // 렌더링
        let lastIndex = 0;
        mergedRanges.forEach(([start, end, types]) => {
          let isCurrentSearchResult = false;
          if (types.includes("search")) {
            const focusedGroup = groupedSearchRanges.find((group) =>
              group.some(([groupStart]) => groupStart === focusedStartIndex),
            );

            if (focusedGroup) {
              isCurrentSearchResult =
                focusedStartIndex !== null &&
                focusedGroup.some(
                  ([groupStart, groupEnd]) =>
                    start >= groupStart && end <= groupEnd,
                );
            } else if (
              currentSearchIndex === 0 &&
              groupedSearchRanges.length > 0
            ) {
              //
            }
          }

          const localStart = start - textStartIndex;
          const localEnd = end - textStartIndex;

          if (localStart > lastIndex) {
            tempInnerNodes.push(
              <span key={`text-${lastIndex}-${localStart}`}>
                {currentNodeValue.slice(lastIndex, localStart)}
              </span>,
            );
          }

          const content = currentNodeValue.slice(localStart, localEnd);

          if (types.includes("label")) {
            const label = regularLabels.find(
              (l) => l.start <= localStart && l.end >= localEnd,
            );

            if (label) {
              const labelElement = (
                <PressLabelElement
                  entity={label.entity}
                  key={`label-${localStart}-${localEnd}`}
                  handleClick={handleLabelClick?.(label)}
                  columnLabelViewerWidth={columnLabelViewerWidth}
                  columnLabelViewerTop={columnLabelViewerTop}
                  searchTerm={searchTerm}
                  isClassificationPage={isClassificationPage}
                >
                  <span
                    style={{
                      fontWeight: types.includes("bold") ? "bold" : "normal",
                      backgroundColor: types.includes("search")
                        ? isCurrentSearchResult
                          ? "#0026FF"
                          : "#6F768B"
                        : "inherit",
                    }}
                  >
                    {content}
                  </span>
                </PressLabelElement>
              );

              if (types.includes("search")) {
                tempInnerNodes.push(
                  <mark
                    data-start-index={start}
                    data-end-index={end}
                    key={`search-${start}-${end}`}
                    style={{
                      backgroundColor: isCurrentSearchResult
                        ? "#0026FF"
                        : "#6F768B",
                      color: "white",
                      position: "relative",
                      display: "inline",
                    }}
                  >
                    {labelElement}
                  </mark>,
                );
              } else {
                tempInnerNodes.push(labelElement);
              }
            }
          } else if (types.includes("search")) {
            tempInnerNodes.push(
              <mark
                data-start-index={start}
                data-end-index={end}
                key={`search-${start}-${end}`}
                style={{
                  backgroundColor: isCurrentSearchResult
                    ? "#0026FF"
                    : "#6F768B",
                  color: "white",
                  position: "relative",
                  display: "inline",
                }}
              >
                <span
                  style={{
                    fontWeight: types.includes("bold") ? "bold" : "normal",
                    backgroundColor: "inherit",
                  }}
                >
                  {content}
                </span>
              </mark>,
            );
          } else if (types.includes("bold")) {
            tempInnerNodes.push(
              <span
                key={`bold-${localStart}-${localEnd}`}
                style={{ fontWeight: "bold" }}
              >
                {content}
              </span>,
            );
          }

          lastIndex = localEnd;
        });

        if (lastIndex < currentNodeValue.length) {
          tempInnerNodes.push(
            <span key={`text-${lastIndex}-${currentNodeValue.length}`}>
              {currentNodeValue.slice(lastIndex)}
            </span>,
          );
        }
      }
      return tempInnerNodes.filter((node) => node !== "");
    },
    [
      searchTerm,
      checkBoldKeyword,
      handleLabelClick,
      columnLabelViewerWidth,
      columnLabelViewerTop,
      isClassificationPage,
      focusedStartIndex,
      currentSearchIndex,
    ],
  );

  const handleAddLabelNodes = useCallback(() => {
    let textIndex = 0;
    let currentTextStartIndex = 0;
    const newInnerNodes: React.ReactNode[] = [];

    parsedContentArray.forEach((contentItem, nodeIndex) => {
      if (contentItem.type === "txt") {
        const firstNodeValue = textContent[textIndex];
        if (!firstNodeValue || contentItem.data === "") return;

        const tempInnerNodes = handleAddTextNodes(
          firstNodeValue,
          sliceLabelData[textIndex],
          currentTextStartIndex,
        );

        newInnerNodes.push(tempInnerNodes.filter((node) => node !== ""));
        textIndex++;
        currentTextStartIndex += firstNodeValue.length;
      } else {
        const tempInnerNodes = handleAddTableNodes(contentItem, nodeIndex);
        newInnerNodes.push(tempInnerNodes);
      }
    });

    setInnerNodes(newInnerNodes);
  }, [
    parsedContentArray,
    textContent,
    handleAddTextNodes,
    sliceLabelData,
    handleAddTableNodes,
  ]);

  useEffect(() => {
    if (onSearchResultsCount) {
      if (!searchTerm) {
        onSearchResultsCount(0);
        setTextSearchCount(0);
        setTableSearchCount(0);
        totalSearchCountRef.current = 0;
        return;
      }

      if (prevSearchTermRef.current !== searchTerm) {
        setTextSearchCount(0);
        setTableSearchCount(0);
        totalSearchCountRef.current = 0;
        tableSearchCountsRef.current = {};
        prevSearchTermRef.current = searchTerm;
      }

      let totalSearchResults = 0;
      const escapedSearchTerm = escapeRegExp(searchTerm);
      textContent.forEach((text) => {
        const regex = new RegExp(escapedSearchTerm, "gi");
        regex.lastIndex = 0;
        const matches = text.match(regex);
        if (matches) {
          totalSearchResults += matches.length;
        }
      });
      setTextSearchCount(totalSearchResults);
      totalSearchCountRef.current = totalSearchResults + tableSearchCount;
      onSearchResultsCount(totalSearchCountRef.current);
    }
  }, [
    searchTerm,
    textContent,
    onSearchResultsCount,
    tableSearchCount,
    currentSearchIndex,
  ]);

  useEffect(() => {
    if (!parsedContentArray.length || !textContent.length) return;

    // NOTE: 초기 렌더링 시 실행
    if (isInitialRender.current) {
      handleAddLabelNodes();
      isInitialRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedContentArray.length, textContent.length]);

  useEffect(() => {
    handleAddLabelNodes();
    // NOTE: 검색어나 포커스 인덱스 변경 시에 다시 받아와야 함.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentSearchIndex, columnLabelViewerWidth]);

  useEffect(() => {
    if (innerNodes.length > 0) {
      const updatedInnerNodes = [...innerNodes];
      const searchNodesWithTable = updatedInnerNodes.map((innerNodesArray) => {
        if (
          Array.isArray(innerNodesArray) &&
          innerNodesArray.length > 0 &&
          innerNodesArray.some((node) => node?.key?.includes("table"))
        ) {
          const tableKey = innerNodesArray?.[0]?.key;
          const tableSearchRange = tableSearchRanges.filter(
            (range) => range.props?.["data-table-key"] === tableKey,
          );
          if (tableSearchRange.length > 0) {
            return [...innerNodesArray, ...tableSearchRange];
          }
          return innerNodesArray;
        }
        return innerNodesArray;
      });

      if (
        JSON.stringify(prevInnerNodesRef.current) !==
        JSON.stringify(searchNodesWithTable)
      ) {
        prevInnerNodesRef.current = searchNodesWithTable;
        onFocusInnerNodesChange?.(searchNodesWithTable);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, tableSearchRanges, onFocusInnerNodesChange]);

  return (
    <S.PressLabelViewer onClick={handleViewerClick} ref={viewerRef}>
      {innerNodes.map((node, index) => (
        <React.Fragment key={`${String(index)}`}>{node}</React.Fragment>
      ))}
    </S.PressLabelViewer>
  );
};

export default PressLabelViewer;
